import { type FC, lazy, Suspense } from "react"

import { type StudentEmailEditorDocument } from "@forento/core/models/studentEmail"

import PartialLoadingPage from "~/components/PartialLoadingPage"

const MjmlContent = lazy(() => import("./MjmlContent"))

/**
 * `editor` carries both: the HTML is what the browser just serialized, for display, while the document is what
 * a test send posts so the server renders the email itself rather than trusting markup from the client.
 */
export type EmailPreviewContent =
	| { type: "editor"; document: StudentEmailEditorDocument; html: string }
	| { type: "mjml"; value: string }
	| { type: "html"; value: string }

type Props = { title?: string; content: EmailPreviewContent }
const EmailPreview: FC<Props> = ({ title, content }) => (
	<div className="flex min-h-0 flex-1 flex-col px-8">
		<div className="border-component-border flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border">
			<div className="flex shrink-0 gap-1.5 bg-neutral-800 px-3 py-2">
				<span className="size-2 rounded-full bg-[#ff5353]" />
				<span className="size-2 rounded-full bg-[#efdd3f]" />
				<span className="size-2 rounded-full bg-[#62ec91]" />
			</div>
			{title && (
				<h3 className="border-component-border shrink-0 border-b bg-white px-4 py-3 font-semibold">{title}</h3>
			)}
			{/*
			 * Both MJML and the editor emit responsive HTML, so the email renders at full width and the iframe is the
			 * only thing that scrolls. It prefers 512px but is allowed to shrink, which keeps a short viewport from
			 * giving the modal a second scrollbar of its own.
			 */}
			<div className="min-h-60 shrink grow basis-128">
				{content.type === "mjml" ? (
					<Suspense fallback={<PartialLoadingPage />}>
						<MjmlContent content={content.value} />
					</Suspense>
				) : (
					<iframe
						className="size-full border-none"
						src={`data:text/html;charset=utf-8,${encodeURIComponent(
							content.type === "html" ? content.value : content.html,
						)}`}
					/>
				)}
			</div>
		</div>
	</div>
)

export default EmailPreview
